<template>
  <v-container
    id="previewInventory"
    fluid
    tag="section"
    class="full-height"
  >
    <base-v-component
      heading="Преглед на инвентаризация"
      goBackText="Обратно към инвентаризации"
      goBackTo="/inventories"
    />

    <v-row class="mt-10 d-print-none">
      <v-col cols="4" class="text-left">
        Създадена от: {{data.createdBy.fullName}} <br />
        Създадена на: {{ISODateString(data.created) }} <br />
        Приключена: {{data.isFinished ? 'Да' : 'Не'}} <br />
        <template v-if="data.isFinished">
          Приключена от: {{data.finishedBy ? data.finishedBy.fullName : ' - '}} <br />
          Приключена на: {{data.finishedOn ? ISODateString(data.finishedOn) : ' - '}}
        </template>

      </v-col>
      <v-col cols="8" class="text-right" v-if="!isFinished">
        <v-btn
          class="primary px-5"
          @click="openImportModal"
          v-if="!isNewDoc"
        >
          <v-icon
            left
            dark
          >
            mdi-upload
          </v-icon>
          Импортиране
        </v-btn>

        <v-btn
          class="error px-5"
          @click="confirmFinishInventory"
          v-if="isReadyToFinish"
        >
          <v-icon
            left
            dark
          >
            mdi-lock
          </v-icon>
          Приключи инвентаризацията
        </v-btn>

        <v-btn
          class="success px-5"
          @click="saveData"
        >
          <v-icon
            left
            dark
          >
            mdi-check
          </v-icon>
          Запази
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mt-10 d-print-none">
      <v-col cols="12" xl="6" lg="6" md="6" class="mx-auto">
        <base-material-card 
          hover-reveal
          icon="mdi-clipboard-check-outline"
          color="primary"
          expandable
          expandableTitle="Детайли"
          :expanded.sync="expandGeneralDetails"
          :areAllCardsExpanded="areAllCardsExpanded"
          @collapseAllCards="collapseAllCards"
          @expandAllCards="expandAllCards"
        >
          <template v-slot:after-heading>
            <div class="font-weight-light card-title mt-2">
              Детайли
            </div>
          </template>

          <v-container class="py-7">
            <v-form
              ref="form"
              v-model="validForm"
              lazy-validation
            >
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    label="Наименование"
                    v-model="data.name"
                    :rules="[rules.required, rules.min]"
                    :readonly="isFinished"
                  />
                </v-col>
                <v-col cols="12">
                  <base-material-date-picker 
                    v-model="data.date"
                    label="Дата"
                    :readonly="isFinished"
                    :is-clearable="!isFinished"
                    :disabled="isFinished"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    label="Създал"
                    v-model="data.createdByName"
                    readonly
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </base-material-card>
      </v-col>

      <v-col cols="12" xl="6" lg="6" md="6" class="mx-auto" v-if="!isNewDoc">
        <base-material-card 
          hover-reveal
          icon="mdi-clipboard-text-outline"
          color="primary"
          expandable
          expandableTitle="Обобщени данни"
          :expanded.sync="expandSummarizedDetails"
          :areAllCardsExpanded="areAllCardsExpanded"
          @collapseAllCards="collapseAllCards"
          @expandAllCards="expandAllCards"
        >
          <template v-slot:after-heading>
            <div class="font-weight-light card-title mt-2">
              Обобщени данни
            </div>
          </template>
          <v-container class="py-7">
            <v-card-text class="pt-0">
              <v-simple-table>
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td>Общо импортирани артикули:</td>
                      <td class="fw600 py-4">{{data.countImported}} бр.</td>
                    </tr>
                    <tr>
                      <td>Общо намерени:</td>
                      <td class="fw600 py-5">{{data.countFound}} бр.</td>
                    </tr>
                    <tr>
                      <td>Общо липсващи:</td>
                      <td class="fw600 py-5">{{data.countMissing}} бр.</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-container>
        </base-material-card>
      </v-col>
    </v-row>
    <v-row class="mt-10" v-if="!isNewDoc">
      <v-col cols="12" xl="12" lg="12" md="12" class="mx-auto">
        <base-material-card 
          hover-reveal
          icon="mdi-magnify"
          color="primary"
          expandable
          expandableTitle="Търсене на тагове"
          :expanded.sync="expandSearchTags"
          :areAllCardsExpanded="areAllCardsExpanded"
          @collapseAllCards="collapseAllCards"
          @expandAllCards="expandAllCards"
        >
          <template v-slot:after-heading>
            <div class="font-weight-light card-title mt-2">
              Сканиране на тагове {{data.tags.length ? `(${data.tags.length} ${data.tags.length === 1 ? 'сканиран артикул' : 'сканирани артикули'})` : ''}}
              <v-btn
                small
                :color="zebraInventoryState ? 'error' : 'secondary'"
                class="ml-3"
                v-if="zebraIsConnected && !isFinished"
                @click="RFIDInventoryAction"
              >
                {{zebraInventoryState ? 'Спри сканиране' : 'Сканирай с RFID'}}
              </v-btn>
            </div>
          </template>
          <v-container class="py-7">
            <v-row>
              <v-col cols="12">
                <v-combobox
                  label="Тъсене на тагове"
                  v-model="tags"
                  chips
                  :clearable="!isFinished"
                  multiple
                  :delimiters="[' ', '\n']"
                  append-icon=""
                  @change="handleValues"
                  :readonly="isFinished"
                >
                    <template v-slot:selection="{ attrs, item, select, selected }">
                        <v-chip
                          small
                          v-bind="attrs"
                          :input-value="selected"
                          :close="!isFinished"
                          color="primary"
                          @click="select"
                          @click:close="removeTag(item)"
                          >
                          {{ item }}
                        </v-chip>
                    </template>
                </v-combobox>
              </v-col>
            </v-row>
            <v-row v-if="!isFinished && data.tags.length">
              <v-col cols="6" class="mx-auto">
                <v-btn 
                  block
                  color="secondary"
                  @click="doInventory"
                >
                  Инвентаризация
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </base-material-card>
      </v-col>
    </v-row>

    <v-row class="mt-5" v-if="!isNewDoc">
      <v-col md="12">
        <v-tabs
          v-model="currentTab"
          fixed-tabs
          slider-color="primary"
          @change="tabChange"
          class="d-print-none"
        >
          <template v-for="tab in tabs">
            <v-tab
              :key="tab.name"
              :href="'#tab-' + tab.name"
            >
              {{ tab.name }}
            </v-tab>
          </template>
        </v-tabs>
        <v-tabs-items v-model="currentTab">
          <template v-for="tab in tabs">
            <v-tab-item
              :key="tab.name"
              :value="'tab-' + tab.name"
              :transition="false"
            >
              <v-card flat v-if="tables.hasOwnProperty(tab.name)">
                <v-card-text>
                  <template v-if="tab.name === 'Импортирани'">
                    <filter-inventory-items ref="filterImportedItems" class="mb-10 d-print-none" @doFilter="getImportedItems" />
                  </template>
                  <template v-if="tab.name === 'Намерени'">
                    <filter-inventory-items ref="filterFoundItems" class="mb-10 d-print-none" @doFilter="getFoundItems" />
                  </template>
                  <template v-if="tab.name === 'Липсващи'">
                    <filter-inventory-items ref="filterMissingItems" class="mb-10 d-print-none" @doFilter="getMissingItems" />
                  </template>
                  <base-data-table 
                    :id="tab.name"
                    :headers="tables[tab.name].headers" 
                    :pagination="tables[tab.name].pagination" 
                    :data="tables[tab.name].data" 
                    :sortBy.sync="tables[tab.name].sortBy"
                    :sortDesc.sync="tables[tab.name].sortDesc" 
                    @getData="getTablesData" 
                    @doAction="doTableAction"
                    @export="doExport"
                    :exportable="['Намерени', 'Липсващи'].includes(tab.name)"
                  />
                </v-card-text>
              </v-card>
            </v-tab-item>
          </template>
        </v-tabs-items>
      </v-col>
    </v-row>
    <import-file-modal ref="importInventoryFileModal" @uploadFile="uploadFile" />
    <confirmModal ref="confirmModal" />
    <locate-tag-modal ref="locateTagModal" />
  </v-container>
</template>

<script>

import store from '@/store';
import { mapGetters } from "vuex";
import moment from "moment";
import { filterInventoryItems, importFileModal, confirmModal, locateTagModal } from '@/components'
import { downloadFileFromResponse, ISODateString } from '@/utils';
import { getInventory, getInventoryFoundItems, getInventoryImportedItems, getInventoryMissingItems, importItems, doInventory, saveInventory, exportInventoryFoundItems, exportInventoryMissingItems, doFinishInventory } from '@/api/inventories';
export default {
  name: "previewInventory",
  components: {
    filterInventoryItems,
    importFileModal,
    locateTagModal,
    confirmModal
  },
  data(){
    return {
      rawData: {},
      data: {
        name: '',
        date: '',
        createdBy: '',
        isFinished: true,
        tags: [],
        countImported: 0,
        countFound: 0,
        countMissing: 0
      },
      isNewDoc: true,
      
      validForm: true,
      rules: {
        required: v => !!v || 'Полето е задължително.',
        min: v => (v && v.length >= 3) || 'Полето трябва да съдържа поне 3 символа',
        requiredSelect: v => !isNaN(parseFloat(v)) && v >= 0 && v <= 999 || "Полето е задължително"
      },

      currentTab: 'tab-Импортирани',
      tabs: [
        {name: 'Импортирани', key: 'Imported', hasDataValue: false},
        {name: 'Намерени', key: 'Found', hasDataValue: false},
        {name: 'Липсващи', key: 'Missing', hasDataValue: false}
      ],
      hasTabsData: {},
      tables: {
        'Импортирани': {
          headers: [],
          data: [],
          pagination: {
            itemsPerPage: 10,
            page: 1,
            perPageOptions: [5, 10, 25, 50, 100],
            total: 0
          },
          sortBy: '',
          sortDesc: null
        }
      },
      expandGeneralDetails: true,
      expandSummarizedDetails: true,
      expandSearchTags: true
    }
  },
  computed: {
    ...mapGetters([
      'lastRoute',
      'user',
      'zebraIsConnected',
      'zebraInventoryState',
      'zebraReadTags'
    ]),
    areAllCardsExpanded(){
      return this.expandGeneralDetails || this.expandSummarizedDetails || this.expandSearchTags;
    },
    tags: {
      get: function(){
        return this.data.tags;
      },
      set: function(val){
        let arr = [];
        for(let el of val){
          if(el.includes(', ')){
            arr = arr.concat(el.split(', '));
          } else if(el.includes(' ')){
            arr = arr.concat(el.split(' '))
          } else if(el.includes(',')){
            arr = arr.concat(el.split(','))  
          } else {
            arr.push(el);
          }
        }
        
        this.data.tags = this.removeDuplicates(arr);
      }
    },
    isFinished(){
      if(this.data)
        return this.data.isFinished === 1;
      
      return false;
    },
    isReadyToFinish(){
      return !this.isNewDoc && this.data.countImported && this.tags.length && (this.data.countFound || this.data.countMissing)
    }
  },
  mounted(){
    if(this.$route.params.id != 'create'){
      this.data._id = this.$route.params.id;
      this.isNewDoc = false;
      this.getData();
    } else {
      this.generateNewDoc()
    }
  },
  watch: {
    zebraIsConnected(){
      this.getData();
    },
    zebraReadTags(tags){
      if(this.zebraInventoryState)
        this.data.tags = tags;
    }
  },
  methods: {
    getData(){
      getInventory(this.data._id).then(response => {
        if(response){
          this.data = response;

          if(this.data.isFinished)
            this.expandSearchTags = false;


          for(var i=0;i<this.tabs.length;i++){
            this.tables[this.tabs[i].name] = {
              headers: [],
              data: [],
              pagination: {
                itemsPerPage: 10,
                page: 1,
                perPageOptions: [5, 10, 25, 50, 100],
                total: 0
              },
              sortBy: 'name',
              sortDesc: false
            }

            switch(this.tabs[i].name){
              case 'Импортирани':
                this.tables[this.tabs[i].name].headers = [
                  { text: 'Дата', value: 'date', valueType: "date" },
                  { text: 'Инвентарен номер', value: 'inventoryNumber', valueType: "text" },
                  { text: 'Наименование', value: 'name', valueType: "text" },
                  { text: 'Автор', value: 'author', valueType: "text" },
                  { text: 'Сигнатура', value: 'signature', valueType: "text" },
                  { text: 'Год. Издаване', value: 'publishedYear', valueType: "text" },
                  { text: 'Цена', value: 'price', valueType: "price" },
                  { text: 'Налична', value: 'availability', valueType: "status", align: 'center', class: 'text-center', active: true, notActive: false, statusIcons: true, activeClass: 'fw600 success--text', notActiveClass: 'fw600 error--text' },
                  {
                    sortable: false,
                    text: '',
                    value: '',
                    show: this.zebraIsConnected,
                    valueType: 'button',
                    style: "width: 80px;",
                    class: 'text-right px-0 pr-2',
                    buttons: [
                      {
                        label: '',
                        title: 'Намери артикула',
                        icon: 'mdi-nfc-search-variant',
                        size: 'small',
                        class: 'secondary',
                        click: 'locateEPCTag' 
                      },
                    ]
                  }
                ]
              break;
              case 'Намерени':
                this.tables[this.tabs[i].name].headers = [
                  { text: 'Дата', value: 'date', valueType: "date" },
                  { text: 'Инвентарен номер', value: 'inventoryNumber', valueType: "text" },
                  { text: 'Наименование', value: 'name', valueType: "text" },
                  { text: 'Автор', value: 'author', valueType: "text" },
                  { text: 'Сигнатура', value: 'signature', valueType: "text" },
                  { text: 'Год. Издаване', value: 'publishedYear', valueType: "text" },
                  { text: 'Цена', value: 'price', valueType: "price" },
                  { text: 'Налична', value: 'availability', valueType: "status", align: 'center', class: 'text-center', active: true, notActive: false, statusIcons: true, activeClass: 'fw600 success--text', notActiveClass: 'fw600 error--text' },{
                    sortable: false,
                    text: '',
                    value: '',
                    show: this.zebraIsConnected,
                    valueType: 'button',
                    style: "width: 80px;",
                    class: 'text-right px-0 pr-2',
                    buttons: [
                      {
                        label: '',
                        title: 'Намери артикула',
                        icon: 'mdi-nfc-search-variant',
                        size: 'small',
                        class: 'secondary',
                        click: 'locateEPCTag' 
                      },
                    ]
                  }
                ]
              break;
              case 'Липсващи':
                this.tables[this.tabs[i].name].headers = [
                  { text: 'Дата', value: 'date', valueType: "date" },
                  { text: 'Инвентарен номер', value: 'inventoryNumber', valueType: "text" },
                  { text: 'Наименование', value: 'name', valueType: "text" },
                  { text: 'Автор', value: 'author', valueType: "text" },
                  { text: 'Сигнатура', value: 'signature', valueType: "text" },
                  { text: 'Год. Издаване', value: 'publishedYear', valueType: "text" },
                  { text: 'Цена', value: 'price', valueType: "price" },
                  { text: 'Налична', value: 'availability', valueType: "status", align: 'center', class: 'text-center', active: true, notActive: false, statusIcons: true, activeClass: 'fw600 success--text', notActiveClass: 'fw600 error--text' },{
                    sortable: false,
                    text: '',
                    value: '',
                    show: this.zebraIsConnected,
                    valueType: 'button',
                    style: "width: 80px;",
                    class: 'text-right px-0 pr-2',
                    buttons: [
                      {
                        label: '',
                        title: 'Намери артикула',
                        icon: 'mdi-nfc-search-variant',
                        size: 'small',
                        class: 'secondary',
                        click: 'locateEPCTag' 
                      },
                    ]
                  }
                ]
              break;
            }
          }

          this.getImportedItems();
        } else {
          this.$snotify.error("Документа не е намерен")
          this.generateNewDoc()
        }
      })
    },
    getImportedItems(){
      const tableName = 'Импортирани';
      let query = Object.assign({}, this.$refs.filterImportedItems[0].filters);
      query.inventoryId = this.data._id;

      query.itemsPerPage = this.tables[tableName].pagination.itemsPerPage;
      if(!query.page) query.page = this.tables[tableName].pagination.page;
      query.sortBy = this.tables[tableName].sortBy;
      query.sortDesc = this.tables[tableName].sortDesc;
      
      getInventoryImportedItems(query).then(data => {
        if(data){
          this.tables[tableName].data = data.Items;
          this.tables[tableName].pagination.total = data.Total;
          this.tables[tableName].pagination.page = data.Page;
        }
      })
    },
    getFoundItems(){
      const tableName = 'Намерени';
      let query = Object.assign({}, this.$refs.filterFoundItems[0].filters);
      query.inventoryId = this.data._id;

      query.itemsPerPage = this.tables[tableName].pagination.itemsPerPage;
      if(!query.page) query.page = this.tables[tableName].pagination.page;
      query.sortBy = this.tables[tableName].sortBy;
      query.sortDesc = this.tables[tableName].sortDesc;
      
      getInventoryFoundItems(query).then(data => {
        if(data){
          this.tables[tableName].data = data.Items;
          this.tables[tableName].pagination.total = data.Total;
          this.tables[tableName].pagination.page = data.Page;
          this.$forceUpdate();
        }
      })
    },
    getMissingItems(){
      const tableName = 'Липсващи';
      let query = Object.assign({}, this.$refs.filterMissingItems[0].filters);
      query.inventoryId = this.data._id;

      query.itemsPerPage = this.tables[tableName].pagination.itemsPerPage;
      if(!query.page) query.page = this.tables[tableName].pagination.page;
      query.sortBy = this.tables[tableName].sortBy;
      query.sortDesc = this.tables[tableName].sortDesc;
      
      getInventoryMissingItems(query).then(data => {
        if(data){
          this.tables[tableName].data = data.Items;
          this.tables[tableName].pagination.total = data.Total;
          this.tables[tableName].pagination.page = data.Page;
          this.$forceUpdate();
        }
      })
    },
    generateNewDoc(){
      this.isNewDoc = true;
      this.data.isFinished = false;
      this.data.date = moment().toISOString()
      this.data.createdBy = this.user.id;
      this.data.createdByName = this.user.name;
    },
    saveData(){
      if(this.$refs.form.validate()){
        saveInventory(this.data).then(response => {
          if(response){
            this.data = response;

            if(this.isNewDoc && this.$route.params.id !== response._id){
              this.$router.push({path: `/inventories/${response._id}`})
            } else if(this.isNewDoc && this.$route.params.id === response._id){
              this.isNewDoc = false;
            }
          }
        })
      }
    },
    confirmFinishInventory(){
      let data = {
        title: 'Приключване на инвентаризация',
        body: 'Сигурни ли сте, че искате да приключите инвентаризацията?',
        callback: this.finishInventory
      }
      this.$refs.confirmModal.openModal(data);
    },
    finishInventory(){
      if(!this.isNewDoc && !this.isFinished)
        doFinishInventory({id: this.data._id}).then(result => {
          if(result){
            this.getData();
            this.$refs.confirmModal.open = false;
          }
        })
    },
    openImportModal(){
      this.$refs.importInventoryFileModal.openModal();
    },
    uploadFile(data){
      if(data.excelFile){
        let formData = new FormData();
        formData.append('file', data.excelFile);
        formData.append('inventoryId', this.data._id);
        importItems(formData).then(response => {
          this.data.countImported = response.countImported;
          this.getImportedItems();
          this.$refs.importInventoryFileModal.closeModal()
        })
      }
    },
    getTablesData(){
      switch(this.currentTab.split("tab-").pop()){
        case "Импортирани":
          this.getImportedItems();
        break;
        case "Намерени":
          this.getFoundItems();
        break;
        case "Липсващи":
          this.getMissingItems();
        break;
      }
    },
    doInventory(){
      doInventory(this.data).then(response => {
        this.data.countFound = response.countFound;
        this.data.countMissing = response.countMissing;
        if(this.currentTab.split("tab-").pop() !== 'Импортирани')
          this.getTablesData();
      })
    },
    RFIDInventoryAction(){
      if(this.zebraIsConnected){
        if(!this.zebraInventoryState){
          store.dispatch('zebra/startInventory')
        } else {
          store.dispatch('zebra/stopInventory');
        }
      }
    },
    handleValues(val){
      if(val.includes(' '))[
        val = val.split(' ')
      ]
    },
    locateTag(data){
      this.$refs.locateTagModal.openModal(data);
    },
    doTableAction(actionData){
      switch(actionData.action){
        case 'previewItem':
          this.$refs.itemModal.openModal(actionData.item);
        break;
        case 'locateEPCTag':
          this.locateTag(actionData.item);
        break;
      }
    },
    tabChange(id){
      this.currentTab = id;
      let idName = id.split("tab-")[1];

      switch(idName){
        case "Намерени":
          setTimeout(() => {
            if(!this.tables[idName].data || !this.tables[idName].data.length)
              this.getFoundItems();
          })
        break;
        case "Липсващи":
          setTimeout(() => {
            if(!this.tables[idName].data || !this.tables[idName].data.length)
              this.getMissingItems();
          })
        break;
      }
    },
    doExport(){
      let tabName = this.currentTab.split("tab-")[1];

      let query = Object.assign({}, this.$refs[tabName === 'Намерени' ? 'filterFoundItems' : 'filterMissingItems'][0].filters);
      query.inventoryId = this.data._id;

      query.itemsPerPage = this.tables[tabName].pagination.itemsPerPage;
      if(!query.page) query.page = this.tables[tabName].pagination.page;
      query.sortBy = this.tables[tabName].sortBy;
      query.sortDesc = this.tables[tabName].sortDesc;

      if(tabName === 'Намерени'){
        exportInventoryFoundItems(query).then(response => {
          downloadFileFromResponse(response);
        })
      } else if(tabName === 'Липсващи'){
        exportInventoryMissingItems(query).then(response => {
          downloadFileFromResponse(response);
        })
      }
    },
    expandAllCards(){
      this.expandGeneralDetails = true;
      this.expandSummarizedDetails = true;
      this.expandSearchTags = true;
      this.$forceUpdate();
    },
    collapseAllCards(){
      this.expandGeneralDetails = false;
      this.expandSummarizedDetails = false;
      this.expandSearchTags = false;
      this.$forceUpdate();
    },
    removeTag(tag, idx){
      if(!this.isFinished)
        this.data.tags.splice(idx, 1)
    },
    removeDuplicates(arr) {
        return arr.filter((item,
            index) => arr.indexOf(item) === index);
    },
    ISODateString: ISODateString
  }
}
</script>